import { InputBlock } from '@coast/schemas';
import { useCallback, useContext, useState } from 'react';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBListRow } from '@shared/components/list/row';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, getAnchorName } from '@shared/styles';
import { GooglePlacesAutocompleteSuggestion } from '@shared/util/googleMaps/types';
import {
  useFetchGooglePlacesCoords,
  useGooglePlacesAutocomplete,
} from '@shared/util/googleMaps/useGooglePlacesAutocomplete';
import { useToggle } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useCardFormContext } from '../../contexts/form';
import { AddressValue } from '../../types';
import { getTypoComponent } from '../../utils';
import { renderLabel } from '../input';
import { MBCardInputBlockBase } from '../input/base';
import { AddressContent } from './content';

interface Props {
  block: InputBlock;
}

export function MBCardInputBlockAddress({ block }: Props) {
  const colors = useContext(ColorThemeContext);

  const { setFieldValue, values } = useCardFormContext();
  const address = values[block.name] as AddressValue;

  const modalVisible = useToggle();
  const [searchQuery, setSearchQuery] = useState('');
  const { suggestions, loading } = useGooglePlacesAutocomplete(
    // @ts-expect-error -- strict migration
    !!searchQuery ? searchQuery : undefined,
  );

  const fetchGooglePlacesCoords = useFetchGooglePlacesCoords();
  const handleSelect = useCallback(
    async (suggestion: GooglePlacesAutocompleteSuggestion) => {
      modalVisible.toggleOff();
      const coords = await fetchGooglePlacesCoords(suggestion.googlePlaceId);
      const updatedAddress: AddressValue = { ...suggestion, coords };
      setFieldValue(block.name, updatedAddress);
      setSearchQuery('');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchGooglePlacesCoords, block.name, setFieldValue],
  );

  const clearValue = useCallback(
    () => setFieldValue(block.name, undefined),
    [setFieldValue, block.name],
  );

  const TypoComponent = getTypoComponent(block.textStyle);
  const anchorName = getAnchorName('input-address', block.name);

  return (
    <>
      <MBTouchableOpacity onPress={modalVisible.toggleOn}>
        <View style={{ anchorName }}>
          <MBCardInputBlockBase block={block} renderLabel={renderLabel}>
            {!address ? (
              <TypoComponent color={colors.foregroundInactive}>{block.placeholder}</TypoComponent>
            ) : (
              <AddressContent textStyle={block.textStyle} address={address} onClear={clearValue} />
            )}
          </MBCardInputBlockBase>
        </View>
      </MBTouchableOpacity>
      {modalVisible.isOn && (
        <AnchoredSheet anchorName={anchorName} initialSize="full" onClose={modalVisible.toggleOff}>
          <View padding={1}>
            <MBSearchBar
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoFocus
              placeholder={i18n(`card.blocks.addressInput.searchPlaceholder`)}
            />
          </View>

          {suggestions.map(suggestion => (
            <MBListRow
              image={<MBIconV2 color={colors.foregroundInactive} name="pin-location" />}
              paddingLeft={1}
              key={suggestion.googlePlaceId}
              title={suggestion.title}
              subtitle={suggestion.subtitle}
              isHidingPressableIndicator
              onPress={() => handleSelect(suggestion)}
            />
          ))}
          {loading && <MBLoadingIndicator padding={2} />}
        </AnchoredSheet>
      )}
    </>
  );
}
